import { type QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";

export interface InlineEditVariables {
  id: string;
}

interface InlineEditContext<TRow> {
  snapshot: [QueryKey, TRow[] | undefined][];
}

export interface UseInlineEditMutationOptions<TRow, TVariables extends InlineEditVariables> {
  getRowId: (row: TRow) => string;
  /**
   * Refetched once the server has answered, so values the database derives —
   * a stock position, a document total — catch up with the field that was
   * edited. Defaults to `listKey`.
   */
  invalidateKey?: QueryKey;
  /** Cache subtree holding every cached list this row can appear in. */
  listKey: QueryKey;
  mutationFn: (variables: TVariables) => Promise<unknown>;
  /** The change to show immediately, before the server has agreed to it. */
  patch: (variables: TVariables) => Partial<TRow>;
}

/**
 * A single-field row edit that shows immediately and undoes itself if the
 * server refuses.
 *
 * An inline edit is worth having only if the row changes as the operator
 * commits; waiting for a round trip on every field turns a list into a form.
 * So the cached row is patched first, and the pre-edit rows are kept to be put
 * back on failure.
 *
 * The rejection is deliberately not swallowed: it reaches `mutateAsync`'s
 * caller, which is the cell that raised the edit, and that is what puts the
 * server's own message in front of the operator. An inline edit that fails
 * silently is worse than no inline edit, because the row on screen then
 * disagrees with the database and nothing says so.
 */
export function useInlineEditMutation<TRow, TVariables extends InlineEditVariables>({
  getRowId,
  invalidateKey,
  listKey,
  mutationFn,
  patch,
}: UseInlineEditMutationOptions<TRow, TVariables>) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, TVariables, InlineEditContext<TRow>>({
    mutationFn,
    onError: (_error, _variables, context) => {
      for (const [key, rows] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, rows);
      }
    },
    onMutate: async (variables) => {
      // An in-flight list refetch would land after the patch and overwrite it.
      await queryClient.cancelQueries({ queryKey: listKey });

      const snapshot = queryClient.getQueriesData<TRow[]>({
        queryKey: listKey,
      });
      const changes = patch(variables);

      queryClient.setQueriesData<TRow[]>({ queryKey: listKey }, (rows) =>
        rows?.map((row) => (getRowId(row) === variables.id ? { ...row, ...changes } : row)),
      );

      return { snapshot };
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: invalidateKey ?? listKey }),
  });
}
